import {
  EmissaoStatus,
  EventoExtraordinarioTipo,
  FuncaoParticipante,
  GrupoAcesso,
  PagamentoExtraordinarioRequest,
  PagamentoSolicitarLiquidacaoRequest,
  PagamentoStatus,
  Perfil,
  TipoLiquidacao,
} from 'common/genericTypes';

export interface UsuarioDoEvento {
  grupoAcesso?: string;
  funcao?: string;
  perfil?: string;
}

export interface PagamentoDoEvento {
  status?: string;
  tipo?: string;
  isMultiCustodiante?: boolean;
  tipoLiquidacaoInstrumento?: string;
  instrumentoFinanceiro?: { distribuicaoPublica?: boolean };
}

export interface CriteriosDeRemuneracaoDoEvento {
  percentualAmortizacaoRemanescente?: number;
  valorNominalUnitarioEmissao?: number;
  dataEmissao?: string | number | Date | { valueOf(): number };
}

export interface InstrumentoDoEvento {
  codigo?: string;
  suspenso?: boolean;
  encerrado?: boolean;
  hasPagamentosInadimplentes?: boolean;
  statusEmissao?: string;
  razaoSocialEmissor?: string;
  emissor?: { razaoSocial?: string };
  criteriosRemuneracao?: CriteriosDeRemuneracaoDoEvento;
}

export interface DadosDoSubmitDoEvento {
  temInstrumento: boolean;
  tipo?: EventoExtraordinarioTipo;
  valorPagamento?: number;
  valorPagamentoTotal?: number;
  quantidadeDeComprovantes: number;
  comprovanteObrigatorio: boolean;
  isMultiCustodiante: boolean;
}

export interface DadosDoPayloadDoEvento {
  tipo: EventoExtraordinarioTipo;
  liquidacaoDireta: boolean;
  valorPagamento?: number;
  valorPagamentoTotal?: number;
  percentualCalculado?: number;
  valorJuros?: number;
  valorPremio?: number;
}

export interface TravaDeEnvio {
  tentarIniciar(): boolean;
  finalizar(): void;
}

export const criarTravaDeEnvio = (): TravaDeEnvio => {
  let emEnvio = false;

  return {
    tentarIniciar: () => {
      if (emEnvio) {
        return false;
      }

      emEnvio = true;
      return true;
    },
    finalizar: () => {
      emEnvio = false;
    },
  };
};

const TIPOS_DE_EVENTO_EXTRAORDINARIO: Array<string> = [
  EventoExtraordinarioTipo.Amortizacao,
  EventoExtraordinarioTipo.ResgateAntecipadoTotal,
];

const STATUS_QUE_PERMITEM_SOLICITAR_LIQUIDACAO: Array<string> = [
  PagamentoStatus.Previsto,
  PagamentoStatus.Reprovada,
  PagamentoStatus.ReprovadoAgenteFiduciario,
];

const STATUS_QUE_PERMITEM_CANCELAR_SOLICITACAO: Array<string> = [
  PagamentoStatus.PendenteAprovacao,
  PagamentoStatus.Reprovada,
];

const FUNCOES_QUE_CADASTRAM_EVENTO: Array<string> = [
  FuncaoParticipante.Emissor,
  FuncaoParticipante.Credenciado,
  FuncaoParticipante.Custodiante,
  FuncaoParticipante.Escriturador,
];

export const isUsuarioInternoLaqus = (usuario: UsuarioDoEvento): boolean =>
  usuario.grupoAcesso === GrupoAcesso.Operacoes || usuario.grupoAcesso === GrupoAcesso.CadastroOperacoes;

export const isUsuarioLeitura = (usuario: UsuarioDoEvento): boolean => usuario.perfil === Perfil.Leitura;

export const isEventoExtraordinario = (tipo?: string): boolean =>
  typeof tipo === 'string' && TIPOS_DE_EVENTO_EXTRAORDINARIO.includes(tipo);

export const podeCadastrarEventoExtraordinario = (usuario: UsuarioDoEvento): boolean =>
  !isUsuarioLeitura(usuario) &&
  (isUsuarioInternoLaqus(usuario) ||
    (typeof usuario.funcao === 'string' && FUNCOES_QUE_CADASTRAM_EVENTO.includes(usuario.funcao)));

export const podeSolicitarLiquidacao = (usuario: UsuarioDoEvento, pagamento: PagamentoDoEvento): boolean => {
  const temPapelPermitido =
    podeCadastrarEventoExtraordinario(usuario) || usuario.funcao === FuncaoParticipante.AgenteFiduciario;

  if (isUsuarioLeitura(usuario) || !temPapelPermitido) {
    return false;
  }

  if (!STATUS_QUE_PERMITEM_SOLICITAR_LIQUIDACAO.includes(pagamento.status ?? '')) {
    return false;
  }

  const custodianteAutorizadoNoMultiCustodiante =
    isUsuarioInternoLaqus(usuario) || usuario.funcao === FuncaoParticipante.Custodiante;

  if (pagamento.isMultiCustodiante === true && !custodianteAutorizadoNoMultiCustodiante) {
    return false;
  }

  const custodianteEmDistribuicaoPublica =
    pagamento.instrumentoFinanceiro?.distribuicaoPublica === true && usuario.funcao === FuncaoParticipante.Custodiante;

  if (custodianteEmDistribuicaoPublica) {
    return false;
  }

  return !pagamento.tipoLiquidacaoInstrumento || pagamento.tipoLiquidacaoInstrumento === TipoLiquidacao.Direta;
};

export const podeCancelarSolicitacaoEvento = (usuario: UsuarioDoEvento, pagamento: PagamentoDoEvento): boolean =>
  podeCadastrarEventoExtraordinario(usuario) &&
  STATUS_QUE_PERMITEM_CANCELAR_SOLICITACAO.includes(pagamento.status ?? '') &&
  isEventoExtraordinario(pagamento.tipo);

export const deveReenviarSolicitacao = (pagamento: PagamentoDoEvento): boolean =>
  pagamento.status === PagamentoStatus.Reprovada && isEventoExtraordinario(pagamento.tipo);

export const isInstrumentoMultiCustodiante = (instrumento?: { custodiantes?: Array<unknown> }): boolean =>
  (instrumento?.custodiantes?.length ?? 0) > 1;

const EMISSAO_QUE_ACEITA_EVENTO: Array<string> = [EmissaoStatus.Emitido, EmissaoStatus.ParcialmenteEmitido];

export const isInstrumentoElegivelParaEvento = (instrumento?: InstrumentoDoEvento): boolean =>
  !!instrumento &&
  !instrumento.suspenso &&
  !instrumento.encerrado &&
  !instrumento.hasPagamentosInadimplentes &&
  EMISSAO_QUE_ACEITA_EVENTO.includes(instrumento.statusEmissao ?? '');

export const filtrarInstrumentosElegiveis = <T extends InstrumentoDoEvento>(instrumentos: Array<T>): Array<T> =>
  (instrumentos ?? []).filter(isInstrumentoElegivelParaEvento);

const tempoDaEmissao = (instrumento: InstrumentoDoEvento): number => {
  const data = instrumento.criteriosRemuneracao?.dataEmissao;

  if (!data) {
    return 0;
  }

  const tempo = new Date(data.valueOf()).getTime();

  return Number.isNaN(tempo) ? 0 : tempo;
};

export const ordenarPorEmissaoMaisRecente = <T extends InstrumentoDoEvento>(instrumentos: Array<T>): Array<T> =>
  [...(instrumentos ?? [])].sort((a, b) => tempoDaEmissao(b) - tempoDaEmissao(a));

export const filtrarInstrumentosPorTermo = <T extends InstrumentoDoEvento>(
  instrumentos: Array<T>,
  termo: string,
  limite = 10
): Array<T> => {
  const alvo = (termo ?? '').trim().toUpperCase();
  const candidatos = instrumentos ?? [];
  const casa = (instrumento: T): boolean =>
    !alvo ||
    [instrumento.codigo, instrumento.razaoSocialEmissor, instrumento.emissor?.razaoSocial].some((campo) =>
      (campo ?? '').toUpperCase().includes(alvo)
    );

  return candidatos.filter(casa).slice(0, limite);
};

export const calcularPuRemanescente = (criterios?: CriteriosDeRemuneracaoDoEvento): number => {
  const percentual = criterios?.percentualAmortizacaoRemanescente ?? 0;
  const valorUnitario = criterios?.valorNominalUnitarioEmissao ?? 0;

  return (percentual * valorUnitario) / 100;
};

export const isSubmitDoEventoBloqueado = (usuario: UsuarioDoEvento, dados: DadosDoSubmitDoEvento): boolean => {
  if (!dados.temInstrumento || !dados.tipo) {
    return true;
  }

  if (dados.tipo === EventoExtraordinarioTipo.Amortizacao && !dados.valorPagamento && !dados.valorPagamentoTotal) {
    return true;
  }

  if (dados.comprovanteObrigatorio && dados.quantidadeDeComprovantes === 0) {
    return true;
  }

  return dados.isMultiCustodiante && !isUsuarioInternoLaqus(usuario);
};

const resolverPercentual = (dados: DadosDoPayloadDoEvento): number | undefined =>
  dados.tipo === EventoExtraordinarioTipo.Amortizacao ? dados.percentualCalculado : undefined;

export const montarPayloadDeReenvio = (dados: DadosDoPayloadDoEvento): PagamentoSolicitarLiquidacaoRequest => ({
  liquidacaoDireta: dados.liquidacaoDireta,
  valorPagamento: dados.valorPagamento || undefined,
  percentual: resolverPercentual(dados),
});

export const montarPayloadDeCadastro = (dados: DadosDoPayloadDoEvento): PagamentoExtraordinarioRequest => ({
  tipo: dados.tipo,
  liquidacaoDireta: dados.liquidacaoDireta,
  percentual: resolverPercentual(dados),
  valorPagamento: dados.valorPagamento || undefined,
  valorPagamentoTotal: dados.valorPagamentoTotal || undefined,
  valorJuros: dados.valorJuros,
  valorPremio: dados.valorPremio,
});
